import { useState } from 'react';
import { ConfirmDialog, fieldClass, helpClass, labelClass } from '@timely/ui';
import { BAD_DEBT_MEANING, RANK_WORDS, automaticRankWords, getCustomerPaymentRank, type Customer, type PaymentRank } from '@timely/domain';
import { setPaymentRank } from './useBook';

/** The ranks in the product's one set of words: "Slow payer" for the stored `Late` (Customer 360, C2). */
const WORDS: Record<PaymentRank, string> = RANK_WORDS;

/**
 * How this customer pays, set by a person (0033).
 *
 * "Automatic" is the rank the product works out — late when anything is over
 * 45 days, good otherwise — and is what an account carries until somebody
 * decides otherwise. Bad debt is only ever declared, never calculated
 * (`customer.ts`), and it takes the account out of Today's queue, so it asks
 * first and says plainly what it does and does not do: it is an operational
 * classification, not an accounting write-off (`BAD_DEBT_MEANING`). Every
 * change leaves a line in History and an audit entry.
 */
export function PaymentRankControl({ customer, workspaceId, updatedAt, disabled, onChanged, onError }: {
    customer: Customer;
    workspaceId: string;
    updatedAt: string;
    disabled: boolean;
    onChanged: (message: string) => Promise<void>;
    onError: (message: string) => void;
}) {
    const [saving, setSaving] = useState(false);
    const [confirmBad, setConfirmBad] = useState(false);
    const manual = customer.paymentRank ?? '';
    const { paymentRank: _set, ...unranked } = customer;
    const automatic = getCustomerPaymentRank(unranked);

    const save = async (value: '' | PaymentRank) => {
        setSaving(true);
        try {
            await setPaymentRank(workspaceId, customer.id, value || null, updatedAt);
            await onChanged(value === '' ? `Back to the automatic rank: ${WORDS[automatic].toLowerCase()}.`
                : value === 'Bad' ? 'Marked as bad debt. It is out of Today’s queue; its balance is unchanged.'
                    : `Rank set to ${WORDS[value].toLowerCase()}.`);
        } catch (failed) {
            onError(failed instanceof Error ? failed.message : 'That did not save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="py-1">
            <label htmlFor="payment-rank" className={labelClass}>Payment rank</label>
            <select
                id="payment-rank"
                value={manual}
                disabled={disabled || saving}
                aria-describedby="payment-rank-help"
                onChange={e => {
                    const value = e.target.value as '' | PaymentRank;
                    if (value === 'Bad') { setConfirmBad(true); return; }
                    void save(value);
                }}
                className={fieldClass({ className: 'w-full' })}
            >
                <option value="">{automaticRankWords(automatic)}</option>
                <option value="Good">{WORDS.Good}</option>
                <option value="Late">{WORDS.Late}</option>
                <option value="Bad">{WORDS.Bad}</option>
            </select>
            <p id="payment-rank-help" className={helpClass}>
                {manual === 'Bad' ? `${BAD_DEBT_MEANING} Choose Automatic or another rank to undo it.`
                    : manual ? 'Set by a person. Choose Automatic to let the ageing decide again.'
                        : 'Worked out from the ageing: late when anything is over 45 days.'}
            </p>
            <ConfirmDialog
                open={confirmBad}
                title="Mark this account as bad debt?"
                confirmLabel="Mark as bad debt"
                onCancel={() => setConfirmBad(false)}
                onConfirm={() => { setConfirmBad(false); void save('Bad'); }}
            >
                <div className="space-y-2">
                    <p>
                        {customer.company} will leave Today’s queue. It stays in Customers and in Reports, under “Bad debt”.
                    </p>
                    <p className="font-semibold text-label">{BAD_DEBT_MEANING}</p>
                    <p>
                        Its balance, its ageing and your accounting records are not changed. It can be set back at any time, and the
                        change is recorded in the account’s history with your name.
                    </p>
                </div>
            </ConfirmDialog>
        </div>
    );
}
